import { FC, useMemo } from "react"
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import { GridColDef } from "@mui/x-data-grid-pro"
import { DataGrid, infiniteListboxProps } from "@/core/components"
import {
  useUrlListState,
  useUrlFilters,
  useInfiniteSelectList,
  UrlFilterSchema,
} from "@/core/hooks"
import { getApiErrorMessage } from "@/core/utils"
import { Userbot } from "../../interfaces"
import {
  useMembershipsQuery,
  useAccountQuery,
  useLazyAccountsQuery,
} from "../../store"
import { useAccountsLookup } from "../../hooks"
import { AccountIdentity } from "../identity/accountIdentity"
import { formatPhone, shortId } from "../identity/identityFormat"

const STATUSES: Userbot.ChatMemberState[] = [
  "creator",
  "admin",
  "member",
  "restricted",
  "banned",
  "left",
]

const statusColor = (
  status: Userbot.ChatMemberState | null,
): "primary" | "success" | "error" | "default" => {
  if (status === "creator" || status === "admin") return "primary"
  if (status === "member" || status === "restricted") return "success"
  if (status === "banned") return "error"
  return "default"
}

const accountOptionLabel = (
  a: Userbot.Account | Userbot.AccountFeedItem,
): string => {
  const tgId = a.telegramUserId
  const formattedPhone = formatPhone(a.phone)
  const fullName = [a.firstName, a.lastName].filter(Boolean).join(" ")
  const username = a.username ? `@${a.username}` : ""
  const primaryText = tgId || formattedPhone || (a.id ? shortId(a.id) : "")

  let details = ""
  if (fullName && username) {
    details = `${fullName} (${username})`
  } else if (fullName) {
    details = fullName
  } else if (username) {
    details = username
  } else if (formattedPhone && tgId) {
    details = formattedPhone
  }

  return details ? `${primaryText} · ${details}` : primaryText
}

const MembershipAccountCell: FC<{ accountId: string | null | undefined }> = ({
  accountId,
}) => {
  const { getAccount } = useAccountsLookup()
  const cached = accountId ? getAccount(accountId) : undefined
  const {
    data: fetched,
    isLoading,
    isError,
  } = useAccountQuery({ id: accountId ?? "" }, { skip: !accountId || !!cached })

  if (!accountId) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    )
  }

  const account = cached ?? fetched

  if (account) {
    return <AccountIdentity account={account} compact />
  }

  if (isLoading) {
    return (
      <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
        {shortId(accountId)}
      </Typography>
    )
  }

  if (isError || !account) {
    return (
      <Tooltip title="данные недоступны">
        <Typography
          variant="body2"
          sx={{ fontFamily: "monospace" }}
          color="text.secondary"
        >
          {shortId(accountId)}
        </Typography>
      </Tooltip>
    )
  }

  return null
}

const FILTERS_SCHEMA = {
  accountId: "string",
  chatId: "string",
  status: "string",
} as const satisfies UrlFilterSchema

export const MembershipsTable: FC = () => {
  const { read: readFilters, write: writeFilters } =
    useUrlFilters(FILTERS_SCHEMA)
  const filters = readFilters()
  const list = useUrlListState()

  const [lazyAccounts] = useLazyAccountsQuery()
  const accountsList = useInfiniteSelectList<Userbot.AccountFeedItem>({
    fetchPage: (p) =>
      lazyAccounts({
        limit: p.limit,
        offset: p.offset,
        search: p.search,
      })
        .unwrap()
        .then((res) => ({
          ...res,
          items: res.items.filter((a) => a.id !== null),
        })),
    getId: (a) => a.id ?? "",
  })

  const { data, isFetching, isError, error } = useMembershipsQuery(
    {
      ...list.listArgs,
      accountId: filters.accountId || undefined,
      chatId: filters.chatId || undefined,
      status: (filters.status || undefined) as
        | Userbot.ChatMemberState
        | undefined,
    },
    { pollingInterval: 60000 },
  )

  const setFilter = (patch: Partial<typeof filters>) => {
    writeFilters({ ...filters, ...patch })
    list.setPaginationModel({ page: 0, pageSize: list.pageSize })
  }

  const columns: GridColDef<Userbot.Membership>[] = useMemo(
    () => [
      {
        field: "accountId",
        headerName: "Аккаунт",
        flex: 2,
        sortable: false,
        renderCell: ({ value }) => <MembershipAccountCell accountId={value} />,
      },
      { field: "chatId", headerName: "Chat ID", flex: 2, sortable: false },
      {
        field: "status",
        headerName: "Статус",
        flex: 1,
        sortable: false,
        renderCell: ({ value }) => (
          <Chip
            size="small"
            color={statusColor(value)}
            label={value ?? "неизвестно"}
          />
        ),
      },
      {
        field: "pendingRequest",
        headerName: "Заявка",
        flex: 1,
        sortable: false,
        renderCell: ({ value }) =>
          value ? (
            <Chip size="small" color="warning" label="Заявка отправлена" />
          ) : (
            "—"
          ),
      },
      {
        field: "source",
        headerName: "Источник",
        flex: 1,
        sortable: false,
        renderCell: ({ value }) => (
          <Typography variant="caption" color="text.secondary">
            {value ?? "—"}
          </Typography>
        ),
      },
      {
        field: "checkedAt",
        headerName: "Сверено",
        flex: 2,
        valueGetter: ({ value }) =>
          value ? new Date(value).toLocaleString("ru") : "—",
      },
    ],
    [],
  )

  return (
    <>
      <Stack direction="row" spacing={2} mb={2}>
        <Autocomplete
          options={accountsList.items}
          value={
            accountsList.items.find((a) => a.id === filters.accountId) ?? null
          }
          onChange={(_, v) => setFilter({ accountId: v?.id ?? "" })}
          onInputChange={(_, v, reason) => {
            if (reason === "input") accountsList.setSearch(v)
          }}
          filterOptions={(o) => o}
          getOptionLabel={accountOptionLabel}
          renderOption={(props, option, state) =>
            [props, accountOptionLabel(option), state] as React.ReactNode
          }
          noOptionsText={
            accountsList.error != null
              ? "Не удалось загрузить список"
              : "Ничего не найдено"
          }
          sx={{ minWidth: 220 }}
          renderInput={(params) => (
            <TextField {...params} label="Аккаунт" placeholder="Поиск" />
          )}
          {...infiniteListboxProps({
            hasMore: accountsList.hasMore,
            isFetching: accountsList.isFetching,
            onLoadMore: accountsList.loadMore,
          })}
        />
        <TextField
          label="Chat ID"
          value={filters.chatId ?? ""}
          onChange={(e) => setFilter({ chatId: e.target.value })}
          sx={{ minWidth: 200 }}
        />
        <TextField
          select
          label="Статус"
          value={filters.status ?? ""}
          onChange={(e) => setFilter({ status: e.target.value })}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">Все</MenuItem>
          {STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {isError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {getApiErrorMessage(error)}
        </Alert>
      )}

      <Box height="75vh">
        <DataGrid<Userbot.Membership>
          columns={columns}
          rows={data?.items ?? []}
          getRowId={(row) => row.id}
          loading={isFetching && !data}
          rowCount={data?.total ?? 0}
          filterMode="client"
          {...list.gridProps}
        />
      </Box>
    </>
  )
}
